import * as THREE from 'three';
import { SurfaceType } from '../terrain/SurfaceType';
import type { BallState } from './BallState';
import type { CollisionConfig } from './CollisionResolver';
import type { SurfaceSample } from './TerrainSurfaceProbe';

export class RollingModel {
  constructor(private readonly collisionConfig: CollisionConfig) {}

  apply(state: BallState, sample: SurfaceSample, deltaSeconds: number): void {
    if (sample.properties.type === SurfaceType.Water) {
      state.velocity.set(0, 0, 0);
      state.spin.set(0, 0, 0);
      state.surfaceType = sample.properties.type;
      state.phase = 'resting';
      state.isResting = true;
      return;
    }

    if (state.altitude > this.collisionConfig.radiusMeters * 2 && state.phase !== 'bouncing')
      return;
    state.surfaceType = sample.properties.type;

    const normal = sample.normal;
    const normalVelocity = normal.clone().multiplyScalar(state.velocity.dot(normal));
    const tangentVelocity = state.velocity.clone().sub(normalVelocity);
    const tangentSpeed = tangentVelocity.length();

    if (tangentSpeed > 0.001) {
      const spinGrab = Math.min(8, state.spin.length() * sample.properties.spinGrab * 3.5);
      const decel =
        (sample.properties.rollingResistance * state.rolloutResistanceMultiplier + spinGrab) *
        deltaSeconds;
      const nextSpeed = Math.max(0, tangentSpeed - decel);
      tangentVelocity.multiplyScalar(nextSpeed / tangentSpeed);
    }

    const slopeDirection = new THREE.Vector3(0, -1, 0).projectOnPlane(normal);
    if (slopeDirection.lengthSq() > 0.0001) {
      tangentVelocity.addScaledVector(
        slopeDirection.normalize(),
        9.81 * Math.sin(sample.slopeRadians) * deltaSeconds,
      );
    }

    const verticalRetention = state.phase === 'bouncing' ? 0.22 : 0.05;
    state.velocity.copy(tangentVelocity.add(normalVelocity.multiplyScalar(verticalRetention)));
    state.spin.multiplyScalar(Math.max(0, 1 - sample.properties.friction * deltaSeconds * 1.8));

    if (state.velocity.dot(normal) <= 0.35) state.phase = 'rolling';

    if (
      state.velocity.length() < this.collisionConfig.restSpeedThreshold ||
      tangentVelocity.length() < 0.05
    ) {
      state.velocity.set(0, 0, 0);
      state.spin.multiplyScalar(0);
      state.isResting = true;
      state.phase = 'resting';
    }
  }
}
